import { useState } from 'react';
import { observer } from 'mobx-react';
import { Layout, Menu } from 'antd';
import startCase from 'lodash/startCase';
import styled from 'styled-components';
import { HSMSSiderIcon } from '@components/shared';
import Link from 'next/link';
import { HSMSRoute } from '@app-types/index';
import { useMst } from '@models/Root';
import { useRouter } from 'next/router';

const { Sider } = Layout;
const { SubMenu } = Menu;

const HSMSLogo = styled.div`
  text-align: center;

  img {
    width: 100%;
    padding: 16px 15px;
    max-width: 190px;
  }
`;

const routeSorter = (userRoutes: HSMSRoute[]): HSMSRoute[] => {
  const routes: HSMSRoute[] = [];
  const routesWithParents: HSMSRoute[] = [];

  userRoutes.forEach((route) => {
    if (route.showInMenu) {
      if (route.parent) {
        routesWithParents.push(route);
      } else {
        routes.push(route);
      }
    }
  });

  const sortedRoutesWithParents: HSMSRoute[] = routesWithParents.sort((routeA, routeB) => {
    if (!routeA.order || !routeB.order) {
      return -1;
    }

    return routeA.order - routeB.order;
  });

  sortedRoutesWithParents.forEach((route) => {
    const parentRouteIndex = routes.findIndex((routeData) => routeData.name === route.parent?.name);
    const parentRoute = routes[parentRouteIndex];

    if (parentRoute && parentRoute.children) {
      routes[parentRouteIndex] = {
        ...parentRoute,
        children: [...parentRoute.children, route],
      };
    } else if (parentRoute) {
      routes[parentRouteIndex] = {
        ...parentRoute,
        children: [route],
      };
    }
  });

  // Pull dashboard to the front
  const dashboardIndex = routes.findIndex((route) => route.name === 'dashboard');
  const dashboardElementArray = routes.splice(dashboardIndex, 1);
  routes.unshift(dashboardElementArray[0]);

  return routes.sort((routeA, routeB) => {
    if (!routeA.order || !routeB.order) {
      return -1;
    }

    return routeA.order - routeB.order;
  });
};

export const HSMSSidebar = observer(() => {
  const { user, student } = useMst();
  const currentPath = '';
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const router = useRouter();

  const routes = routeSorter(user && user.firebaseUID !== '' ? user.routes : student.routes);
  // Set the current path to open keys
  const currentParentPath = currentPath.split('/').filter((item) => item !== '')[2];
  if (currentParentPath && !openKeys.find((key) => key === currentParentPath)) {
    setOpenKeys([...openKeys, currentParentPath]);
  }

  const onCollapsed = () => {
    setCollapsed(!collapsed);
    setOpenKeys([]);
  };

  return (
    <Sider collapsible collapsed={collapsed} onCollapse={onCollapsed} width={280} id="app-sidebar">
      <HSMSLogo className="site-logo">
        <Link href="/">
          <img src="/images/wai_at_sliit_logo.png" alt="WAI at SLIIT Logo" />
        </Link>
      </HSMSLogo>

      <Menu
        theme="dark"
        // defaultSelectedKeys={[router.pathname]}
        mode="inline"
        onOpenChange={(newOpenKeys) => {
          setOpenKeys(newOpenKeys as string[]);
        }}
        openKeys={openKeys}
        selectedKeys={[router.pathname]}
      >
        {routes.map((route) => {
          if (route && route.children) {
            return (
              <SubMenu
                key={route.name}
                title={
                  <span>
                    <HSMSSiderIcon className="anticon" name={route.iconName} />
                    <span>{startCase(route.name)}</span>
                  </span>
                }
              >
                {route.children.map((childRoute: HSMSRoute) => (
                  <Menu.Item key={`/${childRoute.path}`}>
                    <Link href={`/${childRoute.path}`}>
                      <a>
                        <HSMSSiderIcon className="anticon" name={childRoute.iconName} />
                        <span>{startCase(childRoute.name)}</span>
                      </a>
                    </Link>
                  </Menu.Item>
                ))}
              </SubMenu>
            );
          }

          if (route?.dummy) {
            return (
              <Menu.Item key={route.name}>
                <HSMSSiderIcon className="anticon" name={route.iconName} />
                <span>{startCase(route.name)}</span>
              </Menu.Item>
            );
          }

          if (route) {
            return (
              <Menu.Item key={route.name}>
                <Link href={route.path === '/' ? route.path : `/${route.path}`}>
                  <a>
                    <HSMSSiderIcon className="anticon" name={route.iconName} />
                    <span>{startCase(route.name)}</span>
                  </a>
                </Link>
              </Menu.Item>
            );
          }
          return <div key="dummy" />;
        })}
      </Menu>
    </Sider>
  );
});
